import { useState, useRef, useEffect } from 'react';
import { useStore } from 'react-redux';
import { startRequest, getSession } from '@wappsto/redux';

const CHUNK_SIZE = 10;
const ERROR = 'error';
const PENDING = 'pending';
const SUCCESS = 'success';

const useShareNetworks = (itemId) => {
  const store = useStore();
  const [status, setStatus] = useState('idle');
  const mounted = useRef(true);
  const error = useRef({ ids: [], status: null, all: false, future: false });

  const handlePromises = (promises, resolve) => {
    Promise.all(promises)
      .then((response) => {
        if (mounted.current) {
          let allFailed = true;
          let ok = true;
          let newStatus;
          response.forEach((r) => {
            if (r.ok) {
              allFailed = false;
            } else {
              ok = false;
              error.current.ids = [...error.current.ids, ...r.options.rawOptions.query.id];
              if (!newStatus) {
                newStatus = r.status;
              }
            }
          });
          if (ok) {
            resolve({ ok: true });
          } else {
            resolve({ ok: false, newStatus, allFailed });
          }
        }
      })
      .catch(() => {
        if (mounted.current) {
          resolve({ ok: false });
        }
      });
  };

  const shareIdsToItem = (ids, restriction) =>
    new Promise((resolve) => {
      const state = store.getState();
      const session = getSession(state);
      const promises = [];
      for (let i = 0; i * CHUNK_SIZE < ids.length; i += 1) {
        const chunkIds = ids.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const options = {
          url: '/acl',
          method: 'PATCH',
          body: {
            permission: [
              {
                meta: {
                  id: itemId,
                },
                restriction: [restriction],
              },
            ],
          },
          query: {
            propagate: true,
            id: chunkIds,
          },
        };
        promises.push(startRequest(store.dispatch, options, session));
      }
      handlePromises(promises, resolve);
    });

  const shareAllIds = (url, offset, restriction) =>
    new Promise((resolve) => {
      const state = store.getState();
      const session = getSession(state);
      const options = {
        method: 'GET',
        url,
        query: {
          offset,
        },
      };
      startRequest(store.dispatch, options, session)
        .then((response) => {
          if (mounted.current) {
            if (response.ok && response.json) {
              if (response.json.id.length) {
                const promises = [];
                promises.push(shareIdsToItem(response.json.id, restriction));
                const currentItems =
                  response.options.rawOptions.query.offset + response.json.id.length;
                if (currentItems < response.json.count) {
                  promises.push(shareAllIds(url, currentItems, restriction));
                }
                handlePromises(promises, resolve);
              } else {
                resolve({ ok: false, allFailed: true });
              }
            } else {
              resolve({ ok: false, status: response.status, allFailed: true });
            }
          }
        })
        .catch(() => {
          if (mounted.current) {
            resolve({ ok: false });
          }
        });
    });

  const shareTypeWithFuture = (type, filter, restriction, applyToOld) => {
    const state = store.getState();
    const session = getSession(state);
    const url = '/permission_rule';
    const prFilter = [];
    Object.keys(filter).forEach((key) => {
      prFilter.push({ attribute: key, value: filter[key] });
    });
    const options = {
      method: 'POST',
      url,
      body: {
        meta: {
          id: itemId,
        },
        to: [
          {
            type,
            restriction,
            filter: prFilter,
          },
        ],
      },
    };
    if (applyToOld) {
      (options.body as any).apply_to_old = true;
    }
    return startRequest(store.dispatch, options, session);
  };

  const share = (selectedIds, future, type, filter, restriction) => {
    error.current = { ids: [], status: null, all: false, future: false };
    setStatus(PENDING);
    const promises = [];
    if (selectedIds.constructor === Array) {
      promises.push(shareIdsToItem(selectedIds, restriction));
      if (future) {
        promises.push(shareTypeWithFuture(type, filter, restriction, false));
      }
    } else {
      let url = selectedIds
        .replace(/expand=[0-9]+&?/g, '')
        .replace(/limit=[0-9]+&?/g, '')
        .replace(/offset=[0-9]+&?/g, '')
        .replace(/from_last=true&?/g, '')
        .replace(/verbose=true&?/g, '');
      if (url.endsWith('&')) {
        url = url.slice(0, -1);
      }
      if (future) {
        promises.push(shareTypeWithFuture(type, filter, restriction, true));
      } else {
        promises.push(shareAllIds(url, 0, restriction));
      }
    }
    Promise.all(promises)
      .then((response) => {
        if (mounted.current) {
          error.current.all = true;
          let ok = true;
          response.forEach((r) => {
            if (r.ok) {
              error.current.all = false;
            } else {
              ok = false;
              if (r.allFailed !== undefined) {
                if (!r.allFailed) {
                  error.current.all = false;
                }
              } else {
                error.current.future = true;
              }
              if (!error.current.status) {
                error.current.status = r.status;
              }
            }
          });
          if (ok) {
            setStatus(SUCCESS);
          } else {
            setStatus(ERROR);
          }
        }
      })
      .catch(() => {
        if (mounted.current) {
          setStatus(ERROR);
        }
      });
  };

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return [share, status, setStatus, error.current] as const;
};

export default useShareNetworks;
